import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useTheme } from '../App';
import { PageLayout } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { format, subDays, addDays } from 'date-fns';
import {
  TrendingUp, TrendingDown, Target, Clock, Calendar as CalendarIcon, Users, 
  Zap, AlertTriangle, CheckCircle, BarChart3, PieChart, Activity,
  Loader2, RefreshCw, Lightbulb, ArrowRight, Minus, Info, GitCompare,
  ChevronDown
} from 'lucide-react';

import { useWorkAreas } from '../contexts/WorkAreaContext';

const API = '/api';

const getTextColorForBg = (bgColor) => {
  if (!bgColor) return '#000000';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#1e293b' : '#ffffff';
};

// Metric tooltips
const TOOLTIPS = {
  flowScore: "A composite score (0-100) measuring overall productivity health. Components: completion rate (25%), same-day completion (20%), effort accuracy (20%), overload avoidance (15%), reschedule rate (10%), deep work blocks (10%).",
  completionRate: "Percentage of scheduled tasks marked as Done. Higher is better, but 100% might indicate under-scheduling.",
  effortAccuracy: "How well your effort estimates match reality. Calculated only from completed/partial tasks. 100% = perfectly accurate estimates.",
  totalHours: "Total scheduled time across all tasks in the period. Professional hours exclude Personal category tasks.",
  rescheduleRate: "Percentage of tasks moved to a different day via End of Day or manual rescheduling. Lower indicates more realistic planning.",
  deepWorkBlocks: "Number of 2+ hour uninterrupted work blocks (excluding meetings). More deep work = better focus time.",
  meetings: "Total scheduled meetings in the period. High meeting counts can fragment focus time.",
  overloadedDays: "Days with more than 10 hours of professional tasks scheduled. Indicates potential burnout risk.",
  partialProgress: "Tasks where you made progress but didn't complete. These create continuation tasks.",
  workAreaDistribution: "How your actual effort is distributed across work categories. Only includes completed/partial tasks.",
  actualVsEstimated: "Compares effort you logged (actual) vs what you estimated when creating tasks. Only includes completed/partial tasks - future tasks are excluded.",
  rollingAverage: "28-day rolling average smooths out daily variations to show underlying trends.",
};

// Info Icon with Tooltip
const InfoTooltip = ({ content }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="h-3.5 w-3.5 text-slate-400 cursor-help ml-1 inline-block" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs" side="top">
        <p>{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Flow Score Gauge Component
const FlowScoreGauge = ({ score, size = 180 }) => {
  const radius = (size - 20) / 2;
  const circumference = radius * Math.PI;
  const progress = (score / 100) * circumference;
  
  const getColor = (s) => {
    if (s >= 70) return '#10B981';
    if (s >= 40) return '#F59E0B';
    return '#EF4444';
  };
  
  return (
    <div className="relative" style={{ width: size, height: size / 2 + 30 }}>
      <svg width={size} height={size / 2 + 10} className="transform -rotate-0">
        <path
          d={`M 10 ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 10} ${size / 2}`}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <path
          d={`M 10 ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 10} ${size / 2}`}
          fill="none"
          stroke={getColor(score)}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          className="orka-transition-colors duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
        <span className="text-4xl font-bold text-slate-900">{score}</span>
        <span className="text-sm text-slate-500">Flow Score</span>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, trend, color = 'slate', tooltip }) => {
  const colorClasses = {
    slate: 'bg-slate-50 text-slate-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    cyan: 'bg-cyan-50 text-cyan-600'
  };
  
  return (
    <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
      <div className="flex items-start justify-between">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-medium ${
            trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-600' : 'text-slate-400'
          }`}>
            {trend > 0 ? <TrendingUp className="h-3 w-3" /> : 
             trend < 0 ? <TrendingDown className="h-3 w-3" /> : 
             <Minus className="h-3 w-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="mt-3">
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        <div className="text-sm text-slate-500 flex items-center">
          {label}
          {tooltip && <InfoTooltip content={tooltip} />}
        </div>
        {subValue && <div className="text-xs text-slate-400 mt-1">{subValue}</div>}
      </div>
    </div>
  );
};

// Insight Card Component
const InsightCard = ({ insight }) => {
  const iconMap = { success: CheckCircle, warning: AlertTriangle, info: Lightbulb };
  const colorMap = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    info: 'bg-blue-50 border-blue-200 text-blue-700'
  };
  const Icon = iconMap[insight.type] || Lightbulb;
  
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border ${colorMap[insight.type]}`}>
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <p className="text-sm">{insight.message}</p>
    </div>
  );
};

// Vertical Bar Chart Component for Effort with Tooltips
const EffortBarChart = ({ data, showRollingAverage, grouping, excludeWeekends }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.flatMap(d => [d.task_actual_effort || 0, d.estimated_effort || 0, d.rolling_actual_28d || 0]), 1);
  const barWidth = Math.max(16, Math.min(50, 700 / data.length - 4));
  const chartHeight = 220;
  const barAreaHeight = 180;
  
  const handleMouseEnter = (idx, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredIndex(idx);
    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top });
  };
  
  const hoveredItem = hoveredIndex !== null ? data[hoveredIndex] : null;
  
  return (
    <div className="relative">
      <div className="overflow-x-auto">
        <div className="min-w-[600px]" style={{ minWidth: data.length * (barWidth + 4) + 60 }}>
          {/* Y-axis labels */}
          <div className="flex">
            <div className="w-10 flex flex-col justify-between text-xs text-slate-400 pr-2" style={{ height: chartHeight }}>
              <span>{maxValue}</span>
              <span>{Math.round(maxValue * 0.75)}</span>
              <span>{Math.round(maxValue / 2)}</span>
              <span>{Math.round(maxValue * 0.25)}</span>
              <span>0</span>
            </div>
            
            {/* Chart area */}
            <div className="flex-1 relative" style={{ height: chartHeight }}>
              {/* Grid lines */}
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none" style={{ height: barAreaHeight, top: 20 }}>
                <div className="border-b border-slate-100" />
                <div className="border-b border-slate-100" />
                <div className="border-b border-slate-100" />
                <div className="border-b border-slate-100" />
                <div className="border-b border-slate-200" />
              </div>
              
              {/* Bars */}
              <div className="absolute flex items-end gap-1 px-1" style={{ height: barAreaHeight, top: 20, left: 0, right: 0 }}>
                {data.map((item, idx) => {
                  const actualHeight = (item.task_actual_effort / maxValue) * barAreaHeight;
                  const estimatedHeight = (item.estimated_effort / maxValue) * barAreaHeight;
                  const isHovered = hoveredIndex === idx;
                  const isWeekend = item.is_weekend;
                  
                  return (
                    <div 
                      key={idx} 
                      className={`flex flex-col items-center cursor-pointer transition-opacity ${isHovered ? 'opacity-100' : 'opacity-90'}`}
                      style={{ width: barWidth }}
                      onMouseEnter={(e) => handleMouseEnter(idx, e)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <div className="relative flex gap-0.5" style={{ height: barAreaHeight }}>
                        {/* Estimated bar */}
                        <div 
                          className={`absolute bottom-0 left-0 rounded-t orka-transition-colors ${isWeekend ? 'bg-slate-300' : 'bg-slate-200'}`}
                          style={{ height: estimatedHeight, width: barWidth / 2 - 1 }}
                        />
                        {/* Actual bar */}
                        <div 
                          className={`absolute bottom-0 right-0 rounded-t orka-transition-colors ${isWeekend ? 'bg-indigo-400' : 'bg-indigo-500'}`}
                          style={{ height: actualHeight, width: barWidth / 2 - 1 }}
                        />
                        {/* Hover highlight */}
                        {isHovered && (
                          <div className="absolute inset-0 border-2 border-indigo-400 rounded pointer-events-none" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Rolling 28-day average line */}
              {showRollingAverage && data.some(d => d.rolling_actual_28d != null) && (() => {
                const totalWidth = data.length * (barWidth + 4);
                const points = data
                  .map((d, i) => {
                    if (d.rolling_actual_28d == null) return null;
                    const x = i * (barWidth + 4) + barWidth / 2;
                    const y = barAreaHeight - (d.rolling_actual_28d / maxValue) * barAreaHeight;
                    return { x, y, i };
                  })
                  .filter(Boolean);
                
                if (points.length < 2) return null;
                
                return (
                  <svg
                    className="absolute pointer-events-none"
                    style={{ top: 20, left: 4, height: barAreaHeight }}
                    width={totalWidth}
                    height={barAreaHeight}
                  >
                    <polyline
                      points={points.map(p => `${p.x},${p.y}`).join(' ')}
                      fill="none"
                      stroke="#F59E0B"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {points.map(p => (
                      <circle
                        key={p.i}
                        cx={p.x}
                        cy={p.y}
                        r={hoveredIndex === p.i ? 5 : 3}
                        fill="#F59E0B"
                      />
                    ))}
                  </svg>
                );
              })()}
            </div>
          </div>
          
          {/* X-axis date labels */}
          <div className="flex ml-10 gap-1 px-1 mt-1">
            {data.map((item, idx) => {
              // Format label based on grouping
              let label = '';
              let subLabel = '';
              
              if (grouping === 'day' && item.date) {
                const date = new Date(item.date);
                label = `${date.getDate()}`;
                subLabel = item.day_of_week || date.toLocaleDateString('en', { weekday: 'short' }).substring(0, 2);
              } else if (grouping === 'sprint') {
                label = item.period.replace('Sprint ', 'S');
              } else if (grouping === 'week') {
                label = item.period.split(' ')[0];
              } else if (grouping === 'month') {
                label = item.period.substring(0, 3);
              } else {
                label = item.period;
              }
              
              return (
                <div 
                  key={idx} 
                  className={`text-center ${item.is_weekend ? 'text-slate-400' : 'text-slate-600'}`}
                  style={{ width: barWidth }}
                >
                  <div className="text-[10px] font-medium leading-tight">{label}</div>
                  {subLabel && <div className="text-[8px] text-slate-400 leading-tight">{subLabel}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Tooltip */}
      {hoveredItem && (
        <div 
          className="fixed z-50 bg-slate-900 text-white rounded-lg shadow-lg p-3 text-xs pointer-events-none transform -translate-x-1/2 -translate-y-full"
          style={{ 
            left: tooltipPos.x, 
            top: tooltipPos.y - 10,
            minWidth: 180
          }}
        >
          <div className="font-semibold text-sm mb-2 border-b border-slate-700 pb-1">
            {grouping === 'day' && hoveredItem.date 
              ? format(new Date(hoveredItem.date), 'EEE, MMM d, yyyy')
              : hoveredItem.period
            }
            {hoveredItem.is_weekend && <span className="ml-2 text-slate-400">(Weekend)</span>}
          </div>
          
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-slate-400">Actual Effort:</span>
              <span className="font-medium text-indigo-400">{hoveredItem.task_actual_effort} pts</span>
            </div>
            {hoveredItem.work_effort !== undefined && (
              <div className="flex justify-between text-slate-400">
                <span className="pl-2">└ Work:</span>
                <span>{hoveredItem.work_effort} pts</span>
              </div>
            )}
            {hoveredItem.personal_effort !== undefined && hoveredItem.personal_effort > 0 && (
              <div className="flex justify-between text-slate-400">
                <span className="pl-2">└ Personal:</span>
                <span>{hoveredItem.personal_effort} pts</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-400">Estimated:</span>
              <span className="font-medium">{hoveredItem.estimated_effort} pts</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Variance:</span>
              <span className={`font-medium ${hoveredItem.variance > 0 ? 'text-amber-400' : hoveredItem.variance < 0 ? 'text-emerald-400' : ''}`}>
                {hoveredItem.variance > 0 ? '+' : ''}{hoveredItem.variance} pts
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Accuracy:</span>
              <span className="font-medium">{hoveredItem.accuracy}%</span>
            </div>
            {hoveredItem.completed_tasks !== undefined && (
              <div className="flex justify-between border-t border-slate-700 pt-1 mt-1">
                <span className="text-slate-400">Tasks:</span>
                <span>{hoveredItem.completed_tasks} / {hoveredItem.total_tasks} done</span>
              </div>
            )}
            {hoveredItem.rolling_actual_28d !== null && showRollingAverage && (
              <div className="flex justify-between border-t border-slate-700 pt-1 mt-1">
                <span className="text-amber-400">28-day avg:</span>
                <span className="text-amber-400 font-medium">{hoveredItem.rolling_actual_28d} pts/day</span>
              </div>
            )}
          </div>
          
          {/* Arrow */}
          <div className="absolute left-1/2 -bottom-1 transform -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45" />
        </div>
      )}
      
      {/* Legend */}
      <div className="flex justify-center gap-6 mt-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-slate-200 rounded" />
          <span className="text-slate-600">Estimated</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-indigo-500 rounded" />
          <span className="text-slate-600">Actual</span>
        </div>
        {showRollingAverage && (
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-amber-500 rounded" />
            <span className="text-slate-600">28-day rolling avg</span>
          </div>
        )}
        {excludeWeekends && (
          <div className="flex items-center gap-2 text-slate-400">
            <span>(Weekends excluded)</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Work Area Comparison Chart
const WorkAreaComparisonChart = ({ comparison, period1Label, period2Label }) => {
  if (!comparison || comparison.length === 0) return null;
  
  const maxEffort = Math.max(...comparison.flatMap(c => [c.period1_effort, c.period2_effort]), 1);
  
  return (
    <div className="space-y-3">
      {comparison.slice(0, 8).map(item => {
        const p1Width = (item.period1_effort / maxEffort) * 100;
        const p2Width = (item.period2_effort / maxEffort) * 100;
        const bgColor = WORK_AREA_COLORS[item.area] || '#E5E7EB';
        
        return (
          <div key={item.area} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span 
                className="px-2 py-0.5 rounded font-medium"
                style={{ backgroundColor: bgColor, color: getTextColorForBg(bgColor) }}
              >
                {item.area}
              </span>
              <span className={`font-medium ${
                item.percentage_change > 0 ? 'text-emerald-600' : 
                item.percentage_change < 0 ? 'text-red-600' : 'text-slate-500'
              }`}>
                {item.percentage_change > 0 ? '+' : ''}{item.percentage_change}%
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-16">{period1Label}</span>
                <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full orka-transition-colors"
                    style={{ width: `${p1Width}%`, backgroundColor: bgColor }}
                  />
                </div>
                <span className="text-xs text-slate-600 w-12 text-right">{item.period1_effort}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-16">{period2Label}</span>
                <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full orka-transition-colors opacity-60"
                    style={{ width: `${p2Width}%`, backgroundColor: bgColor }}
                  />
                </div>
                <span className="text-xs text-slate-600 w-12 text-right">{item.period2_effort}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Date Range Picker Component
const DateRangePicker = ({ startDate, endDate, onStartChange, onEndChange }) => {
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  
  return (
    <div className="flex items-center gap-2">
      <Popover open={startOpen} onOpenChange={setStartOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 w-32 justify-start text-left font-normal">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {startDate ? format(new Date(startDate), 'MMM d, yy') : 'Start'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[10001]" align="start">
          <Calendar
            mode="single"
            selected={startDate ? new Date(startDate) : undefined}
            onSelect={(date) => {
              onStartChange(date ? format(date, 'yyyy-MM-dd') : '');
              setStartOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <span className="text-slate-400">→</span>
      <Popover open={endOpen} onOpenChange={setEndOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 w-32 justify-start text-left font-normal">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {endDate ? format(new Date(endDate), 'MMM d, yy') : 'End'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[10001]" align="start">
          <Calendar
            mode="single"
            selected={endDate ? new Date(endDate) : undefined}
            onSelect={(date) => {
              onEndChange(date ? format(date, 'yyyy-MM-dd') : '');
              setEndOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Work Area Bar Component
const WorkAreaBar = ({ area, effort, percentage, maxPercentage }) => {
  const bgColor = WORK_AREA_COLORS[area] || '#E5E7EB';
  const width = (percentage / maxPercentage) * 100;
  
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 shrink-0">
        <span 
          className="px-2 py-0.5 rounded text-xs font-medium truncate block"
          style={{ backgroundColor: bgColor, color: getTextColorForBg(bgColor) }}
        >
          {area}
        </span>
      </div>
      <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full orka-transition-colors duration-500"
          style={{ width: `${width}%`, backgroundColor: bgColor }}
        />
      </div>
      <div className="w-20 text-right">
        <span className="text-sm font-medium text-slate-700">{effort} pts</span>
        <span className="text-xs text-slate-400 ml-1">({percentage}%)</span>
      </div>
    </div>
  );
};

// Activity Heatmap Component
const ActivityHeatmap = ({ data }) => {
  if (!data || !data.heatmap) return null;
  
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const maxValue = Math.max(...data.heatmap.flat(), 1);
  
  const getColor = (value) => {
    if (value === 0) return '#F1F5F9';
    const intensity = value / maxValue;
    if (intensity < 0.25) return '#C7D2FE';
    if (intensity < 0.5) return '#A5B4FC';
    if (intensity < 0.75) return '#818CF8';
    return '#6366F1';
  };
  
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        <div className="flex gap-1 mb-1 ml-10">
          {hours.filter((_, i) => i % 3 === 0).map(h => (
            <div key={h} className="w-6 text-center text-xs text-slate-400" style={{ marginLeft: h === 0 ? 0 : 'calc(1.5rem * 2)' }}>
              {h}:00
            </div>
          ))}
        </div>
        {days.map((day, dayIndex) => (
          <div key={day} className="flex items-center gap-1 mb-1">
            <div className="w-8 text-xs text-slate-500 text-right pr-1">{day}</div>
            {hours.map(hour => (
              <div
                key={hour}
                className="w-4 h-4 rounded-sm"
                style={{ backgroundColor: getColor(data.heatmap[dayIndex][hour]) }}
                title={`${day} ${hour}:00 - ${data.heatmap[dayIndex][hour]} tasks`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const AnalyticsPage = () => {
  const { user, actingAs } = useAuth();
  const { theme, setTheme } = useTheme();
  const { workAreaColors } = useWorkAreas();
  const WORK_AREA_COLORS = workAreaColors;
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Date range state
  const [rangeType, setRangeType] = useState('7d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Grouping state
  const [grouping, setGrouping] = useState('day');
  
  // Display options
  const [includePersonal, setIncludePersonal] = useState(true);
  const [showRollingAverage, setShowRollingAverage] = useState(true);
  const [excludeWeekends, setExcludeWeekends] = useState(false);
  
  // Compare mode
  const [compareMode, setCompareMode] = useState(false);
  const [comparePeriod1Start, setComparePeriod1Start] = useState('');
  const [comparePeriod1End, setComparePeriod1End] = useState('');
  const [comparePeriod2Start, setComparePeriod2Start] = useState('');
  const [comparePeriod2End, setComparePeriod2End] = useState('');
  
  // Data state
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [heatmapData, setHeatmapData] = useState(null);
  const [effortChartData, setEffortChartData] = useState(null);
  const [workAreaComparison, setWorkAreaComparison] = useState(null);

  const fetchAnalytics = useCallback(async (showRefresh = false) => {
    if (!user) return;
    
    if (showRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const params = new URLSearchParams({
        include_personal: includePersonal.toString(),
        grouping: grouping
      });
      
      if (rangeType === 'custom' && customStartDate && customEndDate) {
        params.append('range_type', 'custom');
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
      } else {
        params.append('range_type', rangeType);
      }
      
      if (actingAs) params.append('acting_as', actingAs);
      
      // Fetch main dashboard data
      const response = await fetch(`${API}/analytics/dashboard?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
        
        // Fetch effort chart data
        if (result.period) {
          const chartParams = new URLSearchParams({
            start_date: result.period.start,
            end_date: result.period.end,
            grouping: grouping,
            include_personal: includePersonal.toString(),
            exclude_weekends: excludeWeekends.toString()
          });
          if (actingAs) chartParams.append('acting_as', actingAs);
          
          const chartResponse = await fetch(`${API}/analytics/effort-chart?${chartParams}`, {
            credentials: 'include'
          });
          if (chartResponse.ok) {
            const chartResult = await chartResponse.json();
            setEffortChartData(chartResult);
          }
        }
      }
      
      // Fetch insights
      const insightsResponse = await fetch(`${API}/analytics/insights?${params}`, {
        credentials: 'include'
      });
      
      if (insightsResponse.ok) {
        const insightsResult = await insightsResponse.json();
        setInsights(insightsResult.insights || []);
      }
      
      // Fetch heatmap
      const heatmapResponse = await fetch(`${API}/analytics/hourly-heatmap?${params}`, {
        credentials: 'include'
      });
      
      if (heatmapResponse.ok) {
        const heatmapResult = await heatmapResponse.json();
        setHeatmapData(heatmapResult);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, actingAs, rangeType, customStartDate, customEndDate, includePersonal, excludeWeekends, grouping]);

  const fetchComparison = useCallback(async () => {
    if (!compareMode || !comparePeriod1Start || !comparePeriod1End || !comparePeriod2Start || !comparePeriod2End) {
      setWorkAreaComparison(null);
      return;
    }
    
    try {
      const params = new URLSearchParams({
        period1_start: comparePeriod1Start,
        period1_end: comparePeriod1End,
        period2_start: comparePeriod2Start,
        period2_end: comparePeriod2End,
        include_personal: includePersonal.toString()
      });
      if (actingAs) params.append('acting_as', actingAs);
      
      const response = await fetch(`${API}/analytics/work-area-compare?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        setWorkAreaComparison(result);
      }
    } catch (error) {
      console.error('Failed to fetch comparison:', error);
    }
  }, [compareMode, comparePeriod1Start, comparePeriod1End, comparePeriod2Start, comparePeriod2End, includePersonal, actingAs]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  // Set comparison defaults when entering compare mode
  useEffect(() => {
    if (compareMode && !comparePeriod1Start) {
      const today = new Date();
      setComparePeriod1Start(format(subDays(today, 7), 'yyyy-MM-dd'));
      setComparePeriod1End(format(today, 'yyyy-MM-dd'));
      setComparePeriod2Start(format(subDays(today, 14), 'yyyy-MM-dd'));
      setComparePeriod2End(format(subDays(today, 8), 'yyyy-MM-dd'));
    }
  }, [compareMode, comparePeriod1Start]);

  const handleRefresh = () => {
    fetchAnalytics(true);
  };

  if (loading) {
    return (
      <PageLayout user={user} onLogout={() => {}} theme={theme} setTheme={setTheme} title="Analytics">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </PageLayout>
    );
  }

  const completion = data?.completion || {};
  const effort = data?.effort || {};
  const workload = data?.workload || {};
  const meetings = data?.meetings || {};
  const scheduling = data?.scheduling || {};
  const workAreaDist = data?.work_area_id_distribution || {};

  // Calculate max percentage for work area bars
  const totalWorkAreaEffort = Object.values(workAreaDist).reduce((sum, v) => sum + v, 0);
  const workAreaPercentages = Object.entries(workAreaDist).map(([area, eff]) => ({
    area,
    effort: eff,
    percentage: totalWorkAreaEffort > 0 ? Math.round(eff / totalWorkAreaEffort * 100) : 0
  })).sort((a, b) => b.effort - a.effort);
  const maxPercentage = Math.max(...workAreaPercentages.map(w => w.percentage), 1);

  return (
    <PageLayout user={user} onLogout={() => {}} theme={theme} setTheme={setTheme} title="Analytics">
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
              <p className="text-slate-500 text-sm mt-1">
                {data?.period ? `${data.period.start} — ${data.period.end} (${data.period.days} days)` : 'Loading...'}
              </p>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-analytics"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {/* Controls Row */}
          <div className="flex flex-wrap items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
            {/* Date Range Presets */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Period:</span>
              <Select value={rangeType} onValueChange={setRangeType}>
                <SelectTrigger className="w-36 h-9" data-testid="range-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="14d">Last 14 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value="this_week">This week</SelectItem>
                  <SelectItem value="last_week">Last week</SelectItem>
                  <SelectItem value="this_month">This month</SelectItem>
                  <SelectItem value="last_month">Last month</SelectItem>
                  <SelectItem value="this_sprint">This sprint</SelectItem>
                  <SelectItem value="last_sprint">Last sprint</SelectItem>
                  <SelectItem value="custom">Custom...</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom Date Picker */}
            {rangeType === 'custom' && (
              <DateRangePicker
                startDate={customStartDate}
                endDate={customEndDate}
                onStartChange={setCustomStartDate}
                onEndChange={setCustomEndDate}
              />
            )}
            
            <div className="h-6 w-px bg-slate-300" />
            
            {/* Grouping */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Group by:</span>
              <Select value={grouping} onValueChange={setGrouping}>
                <SelectTrigger className="w-28 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="sprint">Sprint</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="h-6 w-px bg-slate-300" />
            
            {/* Toggles */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Personal</span>
              <Switch
                checked={includePersonal}
                onCheckedChange={setIncludePersonal}
                data-testid="include-personal-toggle"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Excl. weekends</span>
              <Switch
                checked={excludeWeekends}
                onCheckedChange={setExcludeWeekends}
                data-testid="exclude-weekends-toggle"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">28d avg</span>
              <Switch
                checked={showRollingAverage}
                onCheckedChange={setShowRollingAverage}
              />
            </div>
            
            <div className="h-6 w-px bg-slate-300" />
            
            {/* Compare Mode */}
            <div className="flex items-center gap-2">
              <GitCompare className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-600">Compare</span>
              <Switch
                checked={compareMode}
                onCheckedChange={setCompareMode}
              />
            </div>
          </div>
          
          {/* Compare Period Selectors */}
          {compareMode && (
            <div className="flex flex-wrap items-center gap-4 p-4 bg-indigo-50 rounded-xl border border-indigo-200">
              <div className="flex items-center gap-2">
                <Badge className="bg-indigo-500">Period 1</Badge>
                <DateRangePicker
                  startDate={comparePeriod1Start}
                  endDate={comparePeriod1End}
                  onStartChange={setComparePeriod1Start}
                  onEndChange={setComparePeriod1End}
                />
              </div>
              <span className="text-indigo-400">vs</span>
              <div className="flex items-center gap-2">
                <Badge className="bg-slate-500">Period 2</Badge>
                <DateRangePicker
                  startDate={comparePeriod2Start}
                  endDate={comparePeriod2End}
                  onStartChange={setComparePeriod2Start}
                  onEndChange={setComparePeriod2End}
                />
              </div>
            </div>
          )}
        </div>

        {/* Flow Score Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Flow Score Card */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                Flow Score
                <InfoTooltip content={TOOLTIPS.flowScore} />
              </h2>
              <Badge className="bg-indigo-100 text-indigo-700">Signature Metric</Badge>
            </div>
            <div className="flex justify-center">
              <FlowScoreGauge score={data?.flow_score || 0} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
              <div>
                <div className="text-slate-500">Completion</div>
                <div className="font-medium text-slate-700">{Math.round((data?.flow_components?.completion_rate || 0) * 100)}%</div>
              </div>
              <div>
                <div className="text-slate-500">Accuracy</div>
                <div className="font-medium text-slate-700">{Math.round((data?.flow_components?.effort_accuracy || 0) * 100)}%</div>
              </div>
              <div>
                <div className="text-slate-500">Deep Work</div>
                <div className="font-medium text-slate-700">{data?.flow_components?.deep_work_blocks || 0}</div>
              </div>
            </div>
          </div>

          {/* Insights Card */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              <h2 className="text-lg font-semibold text-slate-900">Insights</h2>
            </div>
            {insights.length > 0 ? (
              <div className="space-y-3">
                {insights.slice(0, 4).map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Complete more tasks to generate insights</p>
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={CheckCircle} label="Completion Rate" value={`${completion.rate || 0}%`} 
            subValue={`${completion.completed || 0} of ${completion.total_tasks || 0} tasks`} color="emerald"
            tooltip={TOOLTIPS.completionRate} />
          <StatCard icon={Target} label="Effort Accuracy" value={`${effort.accuracy || 100}%`}
            subValue={effort.tendency === 'accurate' ? 'On target' : `Tend to ${effort.tendency}`}
            color={effort.tendency === 'accurate' ? 'emerald' : 'amber'} tooltip={TOOLTIPS.effortAccuracy} />
          <StatCard icon={Clock} label="Total Hours" value={`${workload.total_scheduled_hours || 0}h`}
            subValue={`${workload.professional_hours || 0}h professional`} color="blue" tooltip={TOOLTIPS.totalHours} />
          <StatCard icon={CalendarIcon} label="Reschedule Rate" value={`${scheduling.reschedule_rate || 0}%`}
            subValue={`${scheduling.rescheduled_count || 0} tasks moved`}
            color={scheduling.reschedule_rate > 30 ? 'amber' : 'slate'} tooltip={TOOLTIPS.rescheduleRate} />
        </div>

        {/* Second Row Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Zap} label="Deep Work Blocks" value={workload.deep_work_blocks || 0}
            subValue={`${workload.avg_deep_work_per_day || 0} avg/day`} color="indigo" tooltip={TOOLTIPS.deepWorkBlocks} />
          <StatCard icon={Users} label="Meetings" value={meetings.total_count || 0}
            subValue={`${meetings.avg_per_day || 0} avg/day`} color="cyan" tooltip={TOOLTIPS.meetings} />
          <StatCard icon={AlertTriangle} label="Overloaded Days" value={workload.overload_days || 0}
            subValue={`${workload.overload_rate || 0}% of days >10h`}
            color={workload.overload_rate > 20 ? 'red' : 'slate'} tooltip={TOOLTIPS.overloadedDays} />
          <StatCard icon={Activity} label="Partial Progress" value={completion.partial || 0}
            subValue="Tasks with partial work" color="cyan" tooltip={TOOLTIPS.partialProgress} />
        </div>

        {/* Effort Chart */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center">
              Actual vs Estimated Effort
              <InfoTooltip content={TOOLTIPS.actualVsEstimated} />
            </h3>
            {effortChartData?.totals && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-500">
                  Total: <span className="font-medium text-indigo-600">{effortChartData.totals.actual}</span> actual / 
                  <span className="font-medium text-slate-600"> {effortChartData.totals.estimated}</span> estimated
                </span>
                <span className={`font-medium ${
                  effortChartData.totals.variance > 0 ? 'text-amber-600' : 'text-emerald-600'
                }`}>
                  ({effortChartData.totals.variance > 0 ? '+' : ''}{effortChartData.totals.variance} variance)
                </span>
              </div>
            )}
          </div>
          {effortChartData?.data?.length > 0 ? (
            <EffortBarChart 
              data={effortChartData.data} 
              showRollingAverage={showRollingAverage && effortChartData.has_rolling_average}
              grouping={grouping}
              excludeWeekends={excludeWeekends}
            />
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-400">
              No effort data available
            </div>
          )}
        </div>

        {/* Work Area Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Work Area Distribution */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                Work Area Distribution
                <InfoTooltip content={TOOLTIPS.workAreaDistribution} />
              </h3>
              <span className="text-sm text-slate-500">{totalWorkAreaEffort} pts total</span>
            </div>
            {workAreaPercentages.length > 0 ? (
              <div className="space-y-3">
                {workAreaPercentages.slice(0, 8).map(({ area, effort, percentage }) => (
                  <WorkAreaBar key={area} area={area} effort={effort} percentage={percentage} maxPercentage={maxPercentage} />
                ))}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-slate-400">
                No work area data available
              </div>
            )}
          </div>

          {/* Work Area Comparison (when compare mode is on) */}
          {compareMode && workAreaComparison ? (
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Work Area Comparison</h3>
                <div className="text-xs text-slate-500">
                  P1: {workAreaComparison.period1?.total || 0} pts / P2: {workAreaComparison.period2?.total || 0} pts
                </div>
              </div>
              <WorkAreaComparisonChart 
                comparison={workAreaComparison.comparison} 
                period1Label="P1"
                period2Label="P2"
              />
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Activity Heatmap</h3>
              <p className="text-sm text-slate-500 mb-4">Task scheduling patterns by day and hour</p>
              {heatmapData ? (
                <ActivityHeatmap data={heatmapData} />
              ) : (
                <div className="h-40 flex items-center justify-center text-slate-400">
                  No activity data available
                </div>
              )}
            </div>
          )}
        </div>

        {/* Effort Tracking Summary */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
            Effort Summary
            <InfoTooltip content="Only includes tasks marked Done or Partial. Future/pending tasks are excluded from effort calculations." />
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <div className="text-3xl font-bold text-slate-900">{effort.total_estimated || 0}</div>
              <div className="text-sm text-slate-500">Estimated (completed tasks)</div>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-xl">
              <div className="text-3xl font-bold text-emerald-700">{effort.total_actual || 0}</div>
              <div className="text-sm text-emerald-600">Actual Work Effort</div>
            </div>
            <div className="text-center p-4 bg-sky-50 rounded-xl">
              <div className="text-3xl font-bold text-sky-700">{effort.total_personal || 0}</div>
              <div className="text-sm text-sky-600">Personal Effort</div>
            </div>
          </div>
          
          {effort.tendency && effort.tendency !== 'accurate' && (
            <div className={`mt-4 p-3 rounded-lg ${
              effort.tendency === 'underestimate' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
            }`}>
              <p className="text-sm">
                {effort.tendency === 'underestimate' 
                  ? `You tend to underestimate effort by ${effort.variance_percent}%. Tasks typically take longer than planned.`
                  : `You tend to overestimate effort by ${effort.variance_percent}%. You complete tasks faster than expected!`
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default AnalyticsPage;
